import { ChevronLeft, ChevronRight, MonitorUp } from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Button, VideoView } from '../components/ui';
import { useSettings } from '../lib/settings';
import { useStore } from '../lib/store';
import type { MeetingSession } from '../meeting/MeetingSession';
import { bestGrid, pageSizeFor, paginate, stableOrder, stageOrder } from './layout';
import { VideoTile, type TileProps } from './VideoTile';

export type ViewMode = 'gallery' | 'speaker';

/** Cameras shown beside the main view (speaker view, shared screen); the rest stay off screen. */
const FILMSTRIP_MAX = 12;

/**
 * Tells the session whose cameras are on screen, so only those videos are received. Only when some
 * camera is actually off screen (several gallery pages, a full filmstrip): otherwise everyone is
 * received as usual, which also avoids pausing and resuming videos while the stage settles.
 */
function useVisible(session: MeetingSession, ids: string[] | null, selfId: string, others: number) {
  const shown = ids?.filter((id) => id !== selfId) ?? null;
  const key = shown && shown.length < others ? shown.sort().join(',') : null;
  useEffect(() => {
    session.setVisibleVideo(key === null ? null : key ? key.split(',') : []);
  }, [session, key]);
  useEffect(() => () => session.setVisibleVideo(null), [session]);
}

function useSize<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setSize({ width: entry.contentRect.width, height: entry.contentRect.height }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, size] as const;
}

/** `board`: something shared on the stage (the redistribution board) shown big, with the cameras beside it. */
export function Stage({ session, view, board }: { session: MeetingSession; view: ViewMode; board?: ReactNode }) {
  const s = useStore(session.store);
  const settings = useSettings();
  const selfId = s.selfId!;
  const self = s.participants[selfId];
  const amHost = self?.role === 'host';

  const selfStream = useMemo(() => (s.cameraTrack ? new MediaStream([s.cameraTrack]) : null), [s.cameraTrack]);

  // Gallery order: self first, then everyone else by arrival.
  const people = useMemo(
    () => [self, ...Object.values(s.participants).filter((p) => p.id !== selfId).sort((a, b) => a.joinedAt - b.joinedAt)].filter(Boolean),
    [s.participants, self, selfId],
  );

  const tileProps = (id: string): Omit<TileProps, 'style'> => {
    const p = s.participants[id];
    const isSelf = id === selfId;
    const stream = isSelf ? selfStream : p.cameraStreamId ? (s.remoteStreams[p.cameraStreamId] ?? null) : null;
    return {
      session,
      participant: p,
      stream,
      showVideo: isSelf ? s.videoOn : p.videoOn,
      isSelf,
      speaking: !!s.speaking[id] && !p.audioMuted,
      quality: s.peerStats[id]?.quality,
      peerState: s.peerStats[id]?.state,
      mirror: isSelf && settings.mirrorVideo,
      showName: settings.showNames,
      reactions: s.reactions.filter((r) => r.from === id),
      amHost,
    };
  };

  const sharer = Object.values(s.participants).find((p) => p.sharing);
  const visiblePeople = settings.hideSelfView && people.length > 1 ? people.filter((p) => p.id !== selfId) : people;
  // Large meetings: the filmstrip shows me and whoever spoke recently, not 50 cameras.
  const strip = (exclude?: string) => {
    const list = visiblePeople.filter((p) => p.id !== exclude);
    const ordered = stageOrder(list, selfId, s.spokeAt, FILMSTRIP_MAX, Date.now());
    return { shown: ordered.slice(0, FILMSTRIP_MAX), hidden: Math.max(0, ordered.length - FILMSTRIP_MAX) };
  };
  const more = (n: number) =>
    n > 0 ? (
      <div className="tile filmstrip-more" role="note">
        +{n} {n === 1 ? 'persona' : 'personas'}
      </div>
    ) : null;
  const mode = board ? 'board' : sharer ? 'share' : view === 'speaker' && people.length > 1 ? 'speaker' : 'gallery';
  const mainSpeakerId =
    mode === 'speaker' ? (s.activeSpeakerId && s.participants[s.activeSpeakerId] ? s.activeSpeakerId : people.find((p) => p.id !== selfId)!.id) : null;
  const stripOf = mode === 'gallery' ? null : strip(mainSpeakerId ?? undefined);
  const [galleryIds, setGalleryIds] = useState<string[]>([]);
  // Before the gallery has measured itself it reports nothing yet: leave reception as it is.
  const onScreen =
    mode === 'gallery' ? (galleryIds.length ? galleryIds : null) : [...(mainSpeakerId ? [mainSpeakerId] : []), ...stripOf!.shown.map((p) => p.id)];
  useVisible(session, onScreen, selfId, people.length - 1);

  if (board) {
    return (
      <div className="share-layout board-layout">
        <div className="main-tile">{board}</div>
        <div className="filmstrip" aria-label="Participantes">
          {stripOf!.shown.map((p) => (
            <VideoTile key={p.id} {...tileProps(p.id)} />
          ))}
          {more(stripOf!.hidden)}
        </div>
      </div>
    );
  }

  if (sharer) {
    const isSelfShare = sharer.id === selfId;
    const screenStream = isSelfShare ? s.screenStream : sharer.screenStreamId ? (s.remoteStreams[sharer.screenStreamId] ?? null) : null;
    return (
      <div className="share-layout">
        <div className="main-tile">
          {isSelfShare ? (
            <div className="tile screen" style={{ width: '100%', height: '100%' }}>
              <div className="self-share">
                <MonitorUp size={36} />
                <strong>{s.shareKind === 'image' ? 'Estás compartiendo una imagen' : 'Estás compartiendo tu pantalla'}</strong>
                {s.screenStream && <VideoView stream={s.screenStream} fit="contain" />}
                <Button variant="danger" onClick={() => session.stopShare()}>
                  Dejar de compartir
                </Button>
              </div>
            </div>
          ) : (
            <VideoTile {...tileProps(sharer.id)} stream={screenStream} showVideo={!!screenStream} mirror={false} screen speaking={false} reactions={[]} />
          )}
        </div>
        <div className="filmstrip" aria-label="Participantes">
          {stripOf!.shown.map((p) => (
            <VideoTile key={p.id} {...tileProps(p.id)} />
          ))}
          {more(stripOf!.hidden)}
        </div>
      </div>
    );
  }

  if (mode === 'speaker') {
    const mainId = mainSpeakerId!;
    return (
      <div className="speaker-layout">
        <div className="filmstrip" aria-label="Participantes">
          {stripOf!.shown.map((p) => (
            <VideoTile key={p.id} {...tileProps(p.id)} />
          ))}
          {more(stripOf!.hidden)}
        </div>
        <div className="main-tile">
          <VideoTile key={mainId} {...tileProps(mainId)} />
        </div>
      </div>
    );
  }

  return (
    <Gallery
      people={visiblePeople}
      selfId={selfId}
      spokeAt={s.spokeAt}
      speaking={Object.keys(s.speaking)}
      onPage={setGalleryIds}
      render={(id, style) => <VideoTile key={id} {...tileProps(id)} style={style} />}
    />
  );
}

/**
 * Gallery view. Up to 25 tiles per page (fewer on small screens), like Zoom: me first, then whoever
 * spoke recently, then everyone else by arrival; arrows move between pages.
 */
function Gallery({
  people,
  selfId,
  spokeAt,
  speaking,
  onPage,
  render,
}: {
  people: { id: string; joinedAt: number }[];
  selfId: string;
  spokeAt: Record<string, number>;
  speaking: string[];
  onPage: (ids: string[]) => void;
  render: (id: string, style: React.CSSProperties) => React.ReactNode;
}) {
  const [ref, size] = useSize<HTMLDivElement>();
  const [page, setPage] = useState(0);
  const perPage = pageSizeFor(size.width, size.height);
  // Room for the arrows when there is more than one page.
  const paged = people.length > perPage;
  const capacity = paged ? pageSizeFor(Math.max(0, size.width - 96), size.height) : perPage;
  // Places are kept; someone who talks off the first page is brought onto it (one change every 2 s).
  const order = useRef<string[]>([]);
  const lastSwap = useRef(0);
  const now = Date.now();
  const next = stableOrder(order.current, people, selfId, speaking, spokeAt, capacity, now - lastSwap.current > 2000);
  if (next.slice(0, capacity).join() !== order.current.slice(0, capacity).join() && order.current.length) lastSwap.current = now;
  order.current = next;
  const pages = paginate(next, capacity);
  const current = Math.min(page, pages.length - 1);
  const ids = pages[current];
  const grid = bestGrid(ids.length, paged ? Math.max(0, size.width - 96) : size.width, size.height);
  const key = ids.join(',');
  useEffect(() => {
    if (size.width > 0) onPage(key ? key.split(',') : []);
  }, [key, onPage, size.width]);
  useEffect(() => {
    if (page !== current) setPage(current);
  }, [page, current]);

  return (
    <div ref={ref} className={`gallery ${paged ? 'paged' : ''}`} aria-label="Vista de galería">
      {paged && (
        <button type="button" className="page-btn prev" onClick={() => setPage(current - 1)} disabled={current === 0} aria-label="Página anterior de la galería">
          <ChevronLeft size={22} />
        </button>
      )}
      {size.width > 0 && ids.map((id) => render(id, { width: grid.tileWidth, height: grid.tileHeight }))}
      {paged && (
        <>
          <button
            type="button"
            className="page-btn next"
            onClick={() => setPage(current + 1)}
            disabled={current >= pages.length - 1}
            aria-label="Página siguiente de la galería"
          >
            <ChevronRight size={22} />
          </button>
          <span className="page-count" aria-live="polite">
            {current + 1} / {pages.length}
          </span>
        </>
      )}
    </div>
  );
}
